import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import Header from '../components/Header';
import Footer from '../components/Footer';

const { FiArrowRight, FiClock, FiUser, FiTrendingUp, FiDollarSign, FiShield, FiUsers, FiBarChart3, FiAlertTriangle } = FiIcons;

const BlogPage = () => {
  React.useEffect(() => {
    // SEO Meta Tags
    document.title = "How to Sell an HVAC Business in 2025 | HVACBizBroker";
    
    // Update meta description
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Learn how to maximize valuation and sell your HVAC business confidentially. Expert sell-side advisory from HVACBizBroker.com.');
    } else {
      const meta = document.createElement('meta');
      meta.name = 'description';
      meta.content = 'Learn how to maximize valuation and sell your HVAC business confidentially. Expert sell-side advisory from HVACBizBroker.com.';
      document.getElementsByTagName('head')[0].appendChild(meta);
    }

    // Update meta keywords
    const metaKeywords = document.querySelector('meta[name="keywords"]');
    if (metaKeywords) {
      metaKeywords.setAttribute('content', 'sell HVAC business, HVAC M&A advisor, HVAC business valuation, HVAC exit strategy');
    } else {
      const meta = document.createElement('meta');
      meta.name = 'keywords';
      meta.content = 'sell HVAC business, HVAC M&A advisor, HVAC business valuation, HVAC exit strategy';
      document.getElementsByTagName('head')[0].appendChild(meta);
    }
  }, []);

  const scrollToValuation = () => {
    window.location.href = '/#valuation';
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      
      {/* Blog Hero Section */}
      <section className="bg-gradient-to-br from-gray-900 to-blue-900 text-white py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center"
          >
            <h1 className="text-4xl md:text-5xl font-bold mb-6 leading-tight">
              How to <span className="text-blue-400">Sell an HVAC Business</span> in 2025: A Complete Guide for Owners
            </h1>
            <div className="flex items-center justify-center space-x-6 text-gray-300 mb-8">
              <div className="flex items-center space-x-2">
                <SafeIcon icon={FiUser} className="w-4 h-4" />
                <span>HVAC Biz Broker Team</span>
              </div>
              <div className="flex items-center space-x-2">
                <SafeIcon icon={FiClock} className="w-4 h-4" />
                <span>12 min read</span>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Blog Content */}
      <article className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="prose prose-lg max-w-none"
        >
          {/* Introduction */}
          <div className="bg-blue-50 border-l-4 border-blue-600 p-6 mb-12 rounded-r-lg">
            <p className="text-lg text-gray-700 leading-relaxed">
              <strong>Selling an HVAC business</strong> is one of the most important financial decisions an owner will ever make. 
              With demand for heating, ventilation, and air conditioning services at all-time highs, private equity groups, 
              strategic buyers, and consolidators are actively acquiring firms in the $5M–$50M revenue range. But achieving 
              the best outcome requires preparation, understanding what buyers look for, and structuring a confidential process 
              that maximizes valuation while minimizing disruption to your operations.
            </p>
            <p className="text-lg text-gray-700 mt-4 leading-relaxed">
              At <strong>HVACBizBroker.com</strong>, we specialize in <strong>sell-side advisory for HVAC business owners</strong> who 
              want a trusted expert guiding them through every step — from valuation to closing. Below, we'll walk through the 
              key factors that drive value, the mistakes to avoid, and why now may be the right time to consider an exit.
            </p>
          </div>

          {/* Section 1: Why HVAC Businesses Are in High Demand */}
          <section className="mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-6 flex items-center">
              <SafeIcon icon={FiTrendingUp} className="w-8 h-8 mr-3 text-blue-600" />
              Why HVAC Businesses Are in High Demand
            </h2>
            
            <div className="bg-white p-6 rounded-lg shadow-md mb-6">
              <p className="text-lg text-gray-700 mb-4 leading-relaxed">
                The HVAC industry is experiencing unprecedented growth driven by multiple market forces that make these 
                businesses highly attractive to investors and strategic acquirers.
              </p>
              
              <div className="grid md:grid-cols-3 gap-6 mt-6">
                <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                  <h4 className="text-lg font-semibold text-gray-800 mb-2">Market Growth</h4>
                  <p className="text-gray-700 text-sm">
                    Residential and commercial demand continues rising, fueled by replacement cycles and energy efficiency upgrades.
                  </p>
                </div>
                <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                  <h4 className="text-lg font-semibold text-gray-800 mb-2">PE Consolidation</h4>
                  <p className="text-gray-700 text-sm">
                    Private equity is consolidating fragmented HVAC markets to build regional platforms with economies of scale.
                  </p>
                </div>
                <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
                  <h4 className="text-lg font-semibold text-gray-800 mb-2">Recurring Revenue</h4>
                  <p className="text-gray-700 text-sm">
                    Buyers value recurring service contracts and strong local brand reputation that drives predictable cash flow.
                  </p>
                </div>
              </div>
            </div>

            <p className="text-lg text-gray-700 leading-relaxed">
              This perfect storm of market conditions has created a seller's market where well-positioned <strong>HVAC companies</strong> 
              are commanding premium valuations. Smart buyers recognize that HVAC businesses offer defensive characteristics 
              with essential services that remain in demand regardless of economic cycles.
            </p>
          </section>

          {/* Section 2: Key Valuation Drivers */}
          <section className="mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-6 flex items-center">
              <SafeIcon icon={FiDollarSign} className="w-8 h-8 mr-3 text-blue-600" />
              Key Valuation Drivers for HVAC Firms
            </h2>

            <p className="text-lg text-gray-700 mb-6 leading-relaxed">
              Understanding what drives <strong>HVAC business valuation</strong> is critical for maximizing your exit value. 
              Buyers focus on these five key areas when determining what they're willing to pay:
            </p>

            <div className="space-y-6">
              <div className="bg-white p-6 rounded-lg shadow-md border-l-4 border-green-600">
                <div className="flex items-start space-x-4">
                  <div className="bg-green-100 p-2 rounded-lg">
                    <SafeIcon icon={FiBarChart3} className="w-6 h-6 text-green-600" />
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-gray-800 mb-2">Recurring Revenue</h3>
                    <p className="text-gray-700 mb-3">
                      Maintenance agreements and service contracts can significantly increase valuation multiples. 
                      Businesses with 30%+ recurring revenue often command premium valuations.
                    </p>
                    <div className="text-sm text-gray-600 bg-gray-50 p-3 rounded">
                      <strong>Pro Tip:</strong> Document all recurring contracts and their renewal rates to showcase predictable cash flow.
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-white p-6 rounded-lg shadow-md border-l-4 border-blue-600">
                <div className="flex items-start space-x-4">
                  <div className="bg-blue-100 p-2 rounded-lg">
                    <SafeIcon icon={FiDollarSign} className="w-6 h-6 text-blue-600" />
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-gray-800 mb-2">EBITDA Quality</h3>
                    <p className="text-gray-700 mb-3">
                      Clean financials with normalized owner expenses are essential. Buyers want to see consistent, 
                      growing EBITDA margins that reflect true business performance.
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-white p-6 rounded-lg shadow-md border-l-4 border-purple-600">
                <div className="flex items-start space-x-4">
                  <div className="bg-purple-100 p-2 rounded-lg">
                    <SafeIcon icon={FiUsers} className="w-6 h-6 text-purple-600" />
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-gray-800 mb-2">Customer Concentration</h3>
                    <p className="text-gray-700 mb-3">
                      A broad customer base is better; having one client representing {'>'}30% of revenue is risky and 
                      will negatively impact valuation.
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-white p-6 rounded-lg shadow-md border-l-4 border-orange-600">
                <div className="flex items-start space-x-4">
                  <div className="bg-orange-100 p-2 rounded-lg">
                    <SafeIcon icon={FiShield} className="w-6 h-6 text-orange-600" />
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-gray-800 mb-2">Workforce Stability</h3>
                    <p className="text-gray-700 mb-3">
                      Licensed technicians and experienced management teams add significant value. 
                      Low turnover and strong bench strength are key selling points.
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-white p-6 rounded-lg shadow-md border-l-4 border-red-600">
                <div className="flex items-start space-x-4">
                  <div className="bg-red-100 p-2 rounded-lg">
                    <SafeIcon icon={FiTrendingUp} className="w-6 h-6 text-red-600" />
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-gray-800 mb-2">Geography</h3>
                    <p className="text-gray-700 mb-3">
                      Growing metros (TX, FL, AZ) attract premium valuations due to population growth, 
                      new construction, and favorable business climates.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Section 3: Mistakes to Avoid */}
          <section className="mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-6 flex items-center">
              <SafeIcon icon={FiAlertTriangle} className="w-8 h-8 mr-3 text-red-600" />
              Mistakes Owners Should Avoid
            </h2>

            <p className="text-lg text-gray-700 mb-6 leading-relaxed">
              Even experienced business owners can make costly mistakes during the sale process. Here are the most 
              common pitfalls that can significantly impact your final sale price:
            </p>

            <div className="space-y-6">
              <div className="bg-red-50 p-6 rounded-lg border border-red-200">
                <h3 className="text-xl font-semibold text-red-800 mb-3">❌ Waiting Until Retirement to "Clean Up" the Books</h3>
                <p className="text-gray-700">
                  Many owners postpone financial organization until they're ready to sell. This approach leaves money 
                  on the table and limits your negotiating position. Start preparing 2-3 years in advance.
                </p>
              </div>

              <div className="bg-red-50 p-6 rounded-lg border border-red-200">
                <h3 className="text-xl font-semibold text-red-800 mb-3">❌ Relying on Inbound Buyer Approaches</h3>
                <p className="text-gray-700">
                  Accepting the first offer without creating competitive tension limits your valuation potential. 
                  A structured marketing process with multiple qualified buyers drives better outcomes.
                </p>
              </div>

              <div className="bg-red-50 p-6 rounded-lg border border-red-200">
                <h3 className="text-xl font-semibold text-red-800 mb-3">❌ Not Having Professional Representation</h3>
                <p className="text-gray-700">
                  Going it alone typically leads to lower valuations and unfavorable deal terms. Experienced 
                  <strong> HVAC M&A advisors</strong> add significant value through process management and negotiation expertise.
                </p>
              </div>

              <div className="bg-red-50 p-6 rounded-lg border border-red-200">
                <h3 className="text-xl font-semibold text-red-800 mb-3">❌ Overestimating Value Based on Revenue, Not EBITDA</h3>
                <p className="text-gray-700">
                  Buyers pay for cash flow, not revenue. A $10M revenue company with $500K EBITDA is worth far less 
                  than a $5M revenue company with $1M EBITDA.
                </p>
              </div>
            </div>
          </section>

          {/* Section 4: How the Process Works */}
          <section className="mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-6 flex items-center">
              <SafeIcon icon={FiShield} className="w-8 h-8 mr-3 text-blue-600" />
              How the Process Works
            </h2>

            <p className="text-lg text-gray-700 mb-6 leading-relaxed">
              A successful HVAC business sale follows a structured process designed to maximize value while maintaining 
              confidentiality. Here's how we guide our clients through each phase:
            </p>

            <div className="space-y-6">
              <div className="bg-white p-6 rounded-lg shadow-md">
                <div className="flex items-center mb-4">
                  <div className="w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold mr-4">1</div>
                  <h3 className="text-xl font-semibold text-gray-800">Initial Confidential Consultation and Valuation</h3>
                </div>
                <p className="text-gray-700 ml-12">
                  We start with a comprehensive business assessment to understand your goals, timeline, and current market 
                  position. Our preliminary valuation gives you a realistic expectation of potential sale proceeds.
                </p>
              </div>

              <div className="bg-white p-6 rounded-lg shadow-md">
                <div className="flex items-center mb-4">
                  <div className="w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold mr-4">2</div>
                  <h3 className="text-xl font-semibold text-gray-800">Preparation of Materials (CIM, Financial Recast)</h3>
                </div>
                <p className="text-gray-700 ml-12">
                  We develop a compelling Confidential Information Memorandum and recast your financials to highlight 
                  value drivers and normalize one-time expenses or owner benefits.
                </p>
              </div>

              <div className="bg-white p-6 rounded-lg shadow-md">
                <div className="flex items-center mb-4">
                  <div className="w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold mr-4">3</div>
                  <h3 className="text-xl font-semibold text-gray-800">Strategic Outreach to Vetted Buyers</h3>
                </div>
                <p className="text-gray-700 ml-12">
                  Our extensive buyer network includes strategic acquirers, private equity groups, and individual investors 
                  specifically interested in HVAC businesses in your size range and geography.
                </p>
              </div>

              <div className="bg-white p-6 rounded-lg shadow-md">
                <div className="flex items-center mb-4">
                  <div className="w-8 h-8 bg-green-600 text-white rounded-full flex items-center justify-center font-bold mr-4">4</div>
                  <h3 className="text-xl font-semibold text-gray-800">Negotiating Deal Terms, LOIs, and Closing</h3>
                </div>
                <p className="text-gray-700 ml-12">
                  We manage the entire negotiation process, from initial offers through due diligence to final closing, 
                  ensuring you achieve optimal terms while maintaining business operations.
                </p>
              </div>
            </div>
          </section>

          {/* Conclusion & CTA */}
          <section className="mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">Ready to Explore Your Exit Options?</h2>
            
            <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white p-8 rounded-lg">
              <div className="text-center">
                <h3 className="text-2xl font-bold mb-4">The HVAC Industry is Red Hot for M&A</h3>
                <p className="text-xl mb-6 opacity-90 leading-relaxed">
                  The HVAC industry is one of the hottest areas in middle-market M&A. If you're considering selling 
                  within the next 12–36 months, the best first step is to understand what your business is worth today. 
                  At <strong>HVACBizBroker.com</strong>, we provide confidential valuations and tailored sell-side advisory 
                  so you can make an informed decision on your timing and strategy.
                </p>
                
                <button
                  onClick={scrollToValuation}
                  className="bg-white text-blue-600 hover:bg-gray-100 px-8 py-4 rounded-lg font-semibold text-lg transition-colors duration-300 inline-flex items-center space-x-2 shadow-lg"
                >
                  <span>Schedule a Confidential Valuation</span>
                  <SafeIcon icon={FiArrowRight} className="w-5 h-5" />
                </button>
                
                <p className="text-sm mt-4 opacity-75">
                  Confidential • No Obligation • Industry Experts
                </p>
              </div>
            </div>
          </section>

          {/* Internal Links */}
          <section className="bg-gray-50 p-6 rounded-lg">
            <h3 className="text-xl font-semibold text-gray-800 mb-4">Related Resources</h3>
            <div className="grid md:grid-cols-2 gap-4">
              <Link to="/" className="text-blue-600 hover:text-blue-800 transition-colors">
                ← Back to Homepage
              </Link>
              <button
                onClick={scrollToValuation}
                className="text-blue-600 hover:text-blue-800 transition-colors text-left"
              >
                Get Free Business Valuation →
              </button>
            </div>
          </section>
        </motion.div>
      </article>

      <Footer />
    </div>
  );
};

export default BlogPage;